import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatCurrency } from '../lib/format';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, CheckCircle, XCircle } from 'lucide-react';

export default function LineItemTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');

  const fetchTemplates = () => {
    setLoading(true);
    api.get('/line-item-templates').then(r => {
      setTemplates(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowDialog(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/line-item-templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await api.put(`/line-item-templates/${template.id}`, {
        ...template,
        is_active: !template.is_active,
      });
      toast.success(`Template ${!template.is_active ? 'activated' : 'deactivated'}`);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update template');
    }
  };

  const categories = [...new Set(templates.map(t => t.category))];
  const filteredTemplates = categoryFilter === 'all' 
    ? templates 
    : templates.filter(t => t.category === categoryFilter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="templates-title">Line Item Templates</h1>
          <p className="text-sm text-slate-500 mt-1">Reusable catalog for quick quote/estimate/invoice creation</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreate} data-testid="add-template-button">
          <Plus className="w-4 h-4 mr-2" /> New Template
        </Button>
      </div>

      <div className="flex gap-3">
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48" data-testid="category-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : filteredTemplates.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No templates yet. Create one to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead>Tax</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((t) => (
                  <TableRow key={t.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-slate-900">{t.name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{t.category}</Badge></TableCell>
                    <TableCell className="text-slate-600 text-sm max-w-xs truncate">{t.description || '-'}</TableCell>
                    <TableCell>{t.default_quantity} {t.unit}</TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(t.default_unit_price)}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {t.taxable_gst && 'GST'} {t.taxable_pst && 'PST'}
                      {!t.taxable_gst && !t.taxable_pst && '-'}
                    </TableCell>
                    <TableCell>
                      {t.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />Active
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 text-xs">
                          <XCircle className="w-3 h-3 mr-1" />Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleEdit(t)} 
                          data-testid={`edit-template-${t.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleToggleActive(t)}
                          data-testid={`toggle-template-${t.id}`}
                        >
                          {t.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDelete(t.id)}
                          data-testid={`delete-template-${t.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-rose-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TemplateDialog 
        open={showDialog} 
        onClose={() => setShowDialog(false)} 
        template={editingTemplate}
        onDone={() => { setShowDialog(false); fetchTemplates(); }} 
      />
    </div>
  );
}

function TemplateDialog({ open, onClose, template, onDone }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'General',
    default_quantity: '1',
    default_unit_price: '0',
    unit: 'each',
    taxable_gst: true,
    taxable_pst: false,
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || '',
        description: template.description || '',
        category: template.category || 'General',
        default_quantity: String(template.default_quantity || 1),
        default_unit_price: String(template.default_unit_price || 0),
        unit: template.unit || 'each',
        taxable_gst: template.taxable_gst !== undefined ? template.taxable_gst : true,
        taxable_pst: template.taxable_pst !== undefined ? template.taxable_pst : false,
        is_active: template.is_active !== undefined ? template.is_active : true,
      });
    } else {
      setForm({
        name: '',
        description: '',
        category: 'General',
        default_quantity: '1',
        default_unit_price: '0',
        unit: 'each',
        taxable_gst: true,
        taxable_pst: false,
        is_active: true,
      });
    }
  }, [template, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.default_unit_price) {
      toast.error('Name and unit price are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_quantity: parseFloat(form.default_quantity) || 1,
        default_unit_price: parseFloat(form.default_unit_price) || 0,
      };
      
      if (template) {
        await api.put(`/line-item-templates/${template.id}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/line-item-templates', payload);
        toast.success('Template created');
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Template' : 'Create Template'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input 
              value={form.name} 
              onChange={(e) => setForm({ ...form, name: e.target.value })} 
              placeholder="Hourly Labor Rate" 
              data-testid="template-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea 
              value={form.description} 
              onChange={(e) => setForm({ ...form, description: e.target.value })} 
              placeholder="Standard hourly rate for plumbing work" 
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Input 
                value={form.category} 
                onChange={(e) => setForm({ ...form, category: e.target.value })} 
                placeholder="Labor, Materials, etc." 
                data-testid="template-category-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="template-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="each">each</SelectItem>
                  <SelectItem value="hour">hour</SelectItem>
                  <SelectItem value="sqft">sqft</SelectItem>
                  <SelectItem value="lf">lf (linear ft)</SelectItem>
                  <SelectItem value="unit">unit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Default Quantity *</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={form.default_quantity} 
                onChange={(e) => setForm({ ...form, default_quantity: e.target.value })} 
                data-testid="template-quantity-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Unit Price (CAD) *</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={form.default_unit_price} 
                onChange={(e) => setForm({ ...form, default_unit_price: e.target.value })} 
                data-testid="template-price-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch 
                checked={form.taxable_gst} 
                onCheckedChange={(v) => setForm({ ...form, taxable_gst: v })} 
                data-testid="template-gst-switch"
              />
              <Label>GST</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={form.taxable_pst} 
                onCheckedChange={(v) => setForm({ ...form, taxable_pst: v })} 
                data-testid="template-pst-switch"
              />
              <Label>PST</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={form.is_active} 
                onCheckedChange={(v) => setForm({ ...form, is_active: v })} 
                data-testid="template-active-switch"
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-template-submit">
              {saving ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
